const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://localhost:7222/api';

export interface User {
  strGUID: string;
  strName: string;
  strEmailId: string;
  bolsActive: boolean;
  createDate: string;
  modifyDate: string;
  authProvider: string;
}

export interface LoginRequest {
  Email: string;
  Password: string;
}

export interface RegisterRequest {
  Name: string;
  Email: string;
  Password: string;
  ConfirmPassword: string;
}

export interface ForgotPasswordRequest {
  Email: string;
}

export interface VerifyOTPRequest {
  Email: string;
  OTP: string;
}

export interface ResetPasswordRequest {
  Email: string;
  OTP: string;
  NewPassword: string;
  ConfirmPassword: string;
}

export interface AuthResponse {
  token: string;
  refreshToken: string;
  user: User;
  authProvider: string;
  isNewUser: boolean;
  message: string;
}

export interface OTPResponse {
  success: boolean;
  message: string;
  email: string;
  expiresAt: string | null;
  remainingMinutes: number | null;
  otp?: string; // For testing only
}

export interface FileCategory {
  strGUID: string; // GUID
  strName: string;
  bolIsActive: boolean;
  intSortOrder: number;
  dtCreateDate: Date | string; // Can be Date from backend or string after JSON parsing
  dtModifiedDate: Date | string; // Can be Date from backend or string after JSON parsing
  strCreatedBy: string;
  strModifiedBy: string;
  downloadCount?: number;
}

export interface FileDownload {
  strGUID: string; // GUID
  strCategoryGUID: string; // GUID
  strCategoryName?: string;
  strFileName: string;
  strDisplayName: string;
  strFilePath: string;
  strFileType: string;
  fileSize: number;
  bolisActive: boolean;
  dtUploadDate: Date | string; // Can be Date from backend or string after JSON parsing
  dtModifyDate: Date | string; // Can be Date from backend or string after JSON parsing
  strUploadBy: string;
  strModifiedBy: string;
  downloadCount?: number;
  lastDownloaded?: string;
}

export interface CategoryWithDownloads {
  strGUID: string; // GUID
  strName: string;
  bolIsActive: boolean;
  intSortOrder: number;
  downloads: FileDownload[];
}

// Add FileDocument interface for backward compatibility
export interface FileDocument {
  strGUID: string; // GUID
  strCategoryGUID: string; // GUID
  strCategoryName?: string;
  strFileName: string;
  strDisplayName: string;
  strFilePath: string;
  strFileType: string;
  fileSize: number;
  bolisActive: boolean;
  dtUploadDate: string;
  dtModifyDate: string;
  strUploadBy: string;
  strModifiedBy: string;
  downloadCount?: number;
  lastDownloaded?: string;
}

// Add CategoryWithDocuments interface for backward compatibility
export interface CategoryWithDocuments {
  strGUID: string; // GUID
  strName: string;
  bolIsActive: boolean;
  intSortOrder: number;
  documents: FileDocument[];
}

export interface Circular {
  strGUID: string; // GUID
  dtCircularDate: string;
  strIssuedBy: string;
  strRefNo: string;
  strSubject: string;
  strFileName: string;
  strFilePath: string;
  strFileType: string;
  fileSize: number;
  bolisActive: boolean;
  dtCreatedDate: string;
  dtModifiedDate: string;
  strCreatedBy: string;
  strModifiedBy: string;
  downloadCount?: number;
}

export interface Contact {
  strGUID: string;
  strName: string;
  strEmail: string;
  strSubject: string;
  strMessage: string;
  strPhone: string;
  bolIsRead: boolean;
  bolIsReplied: boolean;
  dtCreatedDate: string;
  dtReadDate?: string;
  dtRepliedDate?: string;
  strAdminReplay?: string;
  strRepliedBy?: string;
  strSource?: string;
  strUserIP: string;
  strUserAgent: string;
}

export interface OtherFile {
  strGUID: string; // GUID
  strTitle: string;
  strModuleType: string; // "Investor Charter - Broking", "Investor Complaint", "AP Branch Details", "KMP Details"
  strFileName: string;
  strFilePath: string;
  strFileType: string;
  filesize: number;
  bolIsActive: boolean;
  dtCreatedDate: string;
  dtModifiedDate: string;
  strCreatedBy: string;
  strModifiedBy: string;
  downloadCount?: number;
}

export interface FileDownloadHistory {
  id: number;
  documentId?: string; // GUID for FileDownload
  circularId?: string; // GUID for Circular
  otherFileId?: string; // GUID for OtherFile
  documentName: string;
  categoryName?: string;
  userGuid: string;
  userName: string;
  userEmail: string;
  downloadDate: string;
  userIP: string;
  userAgent: string;
}

class ApiService {
  private getAuthHeaders(): HeadersInit {
    const token = localStorage.getItem('token');
    return {
      'Content-Type': 'application/json',
      ...(token && { Authorization: `Bearer ${token}` }),
    };
  }

  private async handleResponse<T>(response: Response): Promise<T> {
    if (!response.ok) {
      const errorText = await response.text();
      let errorMessage: string;
      
      try {
        const errorJson = JSON.parse(errorText);
        errorMessage = errorJson.message || errorJson.title || `HTTP ${response.status}: ${response.statusText}`;
      } catch {
        errorMessage = errorText || `HTTP ${response.status}: ${response.statusText}`;
      }
      
      throw new Error(errorMessage);
    }
    
    return response.json();
  }

  private async handleFetchError(error: any): Promise<never> {
    console.error('API Error:', error);
    throw error;
  }

  // Auth Methods
  async login(credentials: LoginRequest): Promise<AuthResponse> {
    try {
      const response = await fetch(`${API_BASE_URL}/Auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(credentials),
      });
      return this.handleResponse<AuthResponse>(response);
    } catch (error) {
      return this.handleFetchError(error);
    }
  }

  async register(userData: RegisterRequest): Promise<AuthResponse> {
    try {
      const response = await fetch(`${API_BASE_URL}/Auth/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(userData),
      });
      return this.handleResponse<AuthResponse>(response);
    } catch (error) {
      return this.handleFetchError(error);
    }
  }

  async forgotPassword(email: string): Promise<OTPResponse> {
    try {
      const response = await fetch(`${API_BASE_URL}/Auth/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include', // Required for cookies in cross-origin requests
        body: JSON.stringify({ Email: email }),
      });
      return this.handleResponse<OTPResponse>(response);
    } catch (error) {
      return this.handleFetchError(error);
    }
  }

  async verifyOTP(email: string, otp: string): Promise<{ success: boolean; message: string; email: string }> {
    try {
      const response = await fetch(`${API_BASE_URL}/Auth/verify-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include', // Required for cookies in cross-origin requests
        body: JSON.stringify({ Email: email, OTP: otp }),
      });
      return this.handleResponse<{ success: boolean; message: string; email: string }>(response);
    } catch (error) {
      return this.handleFetchError(error);
    }
  }

  async resetPassword(email: string, otp: string, newPassword: string): Promise<{ success: boolean; message: string; email: string; redirectToLogin?: boolean }> {
    try {
      const response = await fetch(`${API_BASE_URL}/Auth/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include', // Required for cookies in cross-origin requests
        body: JSON.stringify({ Email: email, OTP: otp, NewPassword: newPassword, ConfirmPassword: newPassword }),
      });
      return this.handleResponse<{ success: boolean; message: string; email: string; redirectToLogin?: boolean }>(response);
    } catch (error) {
      return this.handleFetchError(error);
    }
  }

  async refreshToken(refreshToken: string): Promise<AuthResponse> {
    try {
      const response = await fetch(`${API_BASE_URL}/Auth/refresh-token`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include', // Required for cookies in cross-origin requests
        body: JSON.stringify({ refreshToken }),
      });
      return this.handleResponse<AuthResponse>(response);
    } catch (error) {
      return this.handleFetchError(error);
    }
  }

  async logout(): Promise<{ message: string }> {
    try {
      const response = await fetch(`${API_BASE_URL}/Auth/logout`, {
        method: 'POST',
        headers: this.getAuthHeaders(),
        credentials: 'include', // Required for cookies in cross-origin requests
      });
      localStorage.removeItem('token');
      localStorage.removeItem('refreshToken');
      return this.handleResponse<{ message: string }>(response);
    } catch (error) {
      return this.handleFetchError(error);
    }
  }

  // User Management Methods
  async getProfile(): Promise<User> {
    try {
      const response = await fetch(`${API_BASE_URL}/User/profile`, {
        method: 'GET',
        headers: this.getAuthHeaders(),
      });
      return this.handleResponse<User>(response);
    } catch (error) {
      return this.handleFetchError(error);
    }
  }

  async updateUserProfile(id: string, userData: {
    strName?: string;
    strEmailId?: string;
    bolsActive?: boolean;
    strPassword?: string;
  }): Promise<User> {
    try {
      const response = await fetch(`${API_BASE_URL}/User/${id}`, {
        method: 'PUT',
        headers: this.getAuthHeaders(),
        body: JSON.stringify(userData),
      });
      return this.handleResponse<User>(response);
    } catch (error) {
      return this.handleFetchError(error);
    }
  }

  async changePassword(id: string, currentPassword: string, newPassword: string): Promise<{ message: string }> {
    try {
      const response = await fetch(`${API_BASE_URL}/User/${id}/change-password`, {
        method: 'POST',
        headers: this.getAuthHeaders(),
        body: JSON.stringify({ currentPassword, newPassword }),
      });
      return this.handleResponse<{ message: string }>(response);
    } catch (error) {
      return this.handleFetchError(error);
    }
  }

  getGoogleAuthUrl(): string {
    return `${API_BASE_URL}/Auth/google`;
  }

  // Admin User Management Methods
  async getAllUsers(): Promise<User[]> {
    try {
      const response = await fetch(`${API_BASE_URL}/User`, {
        method: 'GET',
        headers: this.getAuthHeaders(),
      });
      return this.handleResponse<User[]>(response);
    } catch (error) {
      return this.handleFetchError(error);
    }
  }

  async getUserById(id: string): Promise<User> {
    try {
      const response = await fetch(`${API_BASE_URL}/User/${id}`, {
        method: 'GET',
        headers: this.getAuthHeaders(),
      });
      return this.handleResponse<User>(response);
    } catch (error) {
      return this.handleFetchError(error);
    }
  }

  async updateUser(id: string, userData: Partial<User>): Promise<User> {
    try {
      const response = await fetch(`${API_BASE_URL}/User/${id}`, {
        method: 'PUT',
        headers: this.getAuthHeaders(),
        body: JSON.stringify(userData),
      });
      return this.handleResponse<User>(response);
    } catch (error) {
      return this.handleFetchError(error);
    }
  }

  async deleteUser(id: string): Promise<{ message: string }> {
    try {
      const response = await fetch(`${API_BASE_URL}/User/${id}`, {
        method: 'DELETE',
        headers: this.getAuthHeaders(),
      });
      return this.handleResponse<{ message: string }>(response);
    } catch (error) {
      return this.handleFetchError(error);
    }
  }

  // Category Management Methods
  async getAllCategories(includeInactive: boolean = false): Promise<FileCategory[]> {
    try {
      const url = new URL(`${API_BASE_URL}/Download/categories`);
      if (includeInactive) {
        url.searchParams.append('includeInactive', 'true');
      }
      
      const response = await fetch(url.toString(), {
        method: 'GET',
        headers: this.getAuthHeaders(),
      });
      return this.handleResponse<FileCategory[]>(response);
    } catch (error) {
      return this.handleFetchError(error);
    }
  }

  async getCategoryById(id: string): Promise<FileCategory> {
    try {
      const response = await fetch(`${API_BASE_URL}/Download/categories/${id}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        },
      });
      return this.handleResponse<FileCategory>(response);
    } catch (error) {
      return this.handleFetchError(error);
    }
  }

  async getAvailableSortOrders(): Promise<number[]> {
    try {
      const response = await fetch(`${API_BASE_URL}/Download/categories/sort-orders/available`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        },
      });
      return this.handleResponse<number[]>(response);
    } catch (error) {
      return this.handleFetchError(error);
    }
  }

  async getCategoryWithDownloads(id: string): Promise<CategoryWithDownloads> {
    try {
      const response = await fetch(`${API_BASE_URL}/Download/categories/${id}/downloads`, {
        method: 'GET',
        headers: this.getAuthHeaders(),
      });
      return this.handleResponse<CategoryWithDownloads>(response);
    } catch (error) {
      return this.handleFetchError(error);
    }
  }

  // Add getCategoryWithDocuments method for backward compatibility
  async getCategoryWithDocuments(id: string): Promise<CategoryWithDocuments> {
    try {
      // First get the category
      const categoryResponse = await fetch(`${API_BASE_URL}/Download/categories/${id}`, {
        headers: this.getAuthHeaders(),
      });
      const category = await this.handleResponse<FileCategory>(categoryResponse);
      
      // Then get the documents for this category
      const documentsResponse = await fetch(`${API_BASE_URL}/Download/categories/${id}/downloads`, {
        headers: this.getAuthHeaders(),
      });
      const documents = await this.handleResponse<FileDownload[]>(documentsResponse);
      
      // Convert FileDownload to FileDocument format
      const fileDocuments: FileDocument[] = documents.map(doc => ({
        strGUID: doc.strGUID,
        strCategoryGUID: doc.strCategoryGUID,
        strCategoryName: category.strName,
        strFileName: doc.strFileName,
        strDisplayName: doc.strDisplayName,
        strFilePath: doc.strFilePath,
        strFileType: doc.strFileType,
        fileSize: doc.fileSize,
        bolisActive: doc.bolisActive,
        dtUploadDate: typeof doc.dtUploadDate === 'string' ? doc.dtUploadDate : doc.dtUploadDate.toISOString(),
        dtModifyDate: typeof doc.dtModifyDate === 'string' ? doc.dtModifyDate : doc.dtModifyDate.toISOString(),
        strUploadBy: doc.strUploadBy,
        strModifiedBy: doc.strModifiedBy,
        downloadCount: 0 // Default value since FileDownload doesn't have this field
      }));
      
      return {
        strGUID: category.strGUID,
        strName: category.strName,
        bolIsActive: category.bolIsActive,
        intSortOrder: category.intSortOrder,
        documents: fileDocuments
      };
    } catch (error) {
      return this.handleFetchError(error);
    }
  }

  async createCategory(category: {
    strName: string;
    intSortOrder: number;
  }): Promise<FileCategory> {
    try {
      const response = await fetch(`${API_BASE_URL}/Download/categories`, {
        method: 'POST',
        headers: this.getAuthHeaders(),
        body: JSON.stringify(category),
      });
      return this.handleResponse<FileCategory>(response);
    } catch (error) {
      return this.handleFetchError(error);
    }
  }

  async updateCategory(id: string, category: {
    strName: string;
    bolIsActive: boolean;
    intSortOrder: number;
  }): Promise<FileCategory> {
    try {
      console.log('API: Updating category', { id, category, url: `${API_BASE_URL}/Download/categories/${id}` });
      
      const response = await fetch(`${API_BASE_URL}/Download/categories/${id}`, {
        method: 'PUT',
        headers: this.getAuthHeaders(),
        body: JSON.stringify(category),
      });
      
      console.log('API: Update category response status:', response.status);
      
      return this.handleResponse<FileCategory>(response);
    } catch (error) {
      console.error('API: Error updating category:', error);
      return this.handleFetchError(error);
    }
  }

  async deleteCategory(id: string): Promise<void> {
    try {
      const response = await fetch(`${API_BASE_URL}/Download/categories/${id}`, {
        method: 'DELETE',
        headers: this.getAuthHeaders(),
      });
      if (!response.ok) {
        // Try to get the error message from the response
        const errorData = await response.json().catch(() => ({}));
        const errorMessage = errorData.message || `Failed to delete category with ID ${id}`;
        throw new Error(errorMessage);
      }
    } catch (error) {
      return this.handleFetchError(error);
    }
  }

  // Download Management Methods
  async getAllDownloads(): Promise<{ category: FileCategory; downloads: FileDownload[] }[]> {
    try {
      const response = await fetch(`${API_BASE_URL}/Download/documents`, {
        method: 'GET',
        headers: this.getAuthHeaders(),
      });
      const downloads = await this.handleResponse<FileDownload[]>(response);
      
      // Group downloads by category
      const categories = await this.getAllCategories();
      const result = categories.map(category => ({
        category,
        downloads: downloads.filter(download => download.strCategoryGUID === category.strGUID)
      }));
      
      return result;
    } catch (error) {
      return this.handleFetchError(error);
    }
  }

  async getDownloadsByCategory(categoryId: string): Promise<FileDownload[]> {
    try {
      const response = await fetch(`${API_BASE_URL}/Download/documents?categoryId=${categoryId}`, {
        method: 'GET',
        headers: this.getAuthHeaders(),
      });
      return this.handleResponse<FileDownload[]>(response);
    } catch (error) {
      return this.handleFetchError(error);
    }
  }

  async getDownloadById(id: string): Promise<FileDownload> {
    try {
      const response = await fetch(`${API_BASE_URL}/Download/documents/${id}`, {
        method: 'GET',
        headers: this.getAuthHeaders(),
      });
      return this.handleResponse<FileDownload>(response);
    } catch (error) {
      return this.handleFetchError(error);
    }
  }

  async uploadDownload(file: File, categoryId: string, displayName: string): Promise<FileDocument> {
    try {
      const token = localStorage.getItem('token');
      
      const formData = new FormData();
      formData.append('file', file);
      formData.append('categoryId', categoryId);
      formData.append('displayName', displayName);
      
      // Create headers without Content-Type for FormData
      const headers: HeadersInit = {};
      if (token) {
        headers.Authorization = `Bearer ${token}`;
      }
      
      const response = await fetch(`${API_BASE_URL}/Download/documents`, {
        method: 'POST',
        headers,
        body: formData,
      });
      
      return this.handleResponse<FileDocument>(response);
    } catch (error) {
      return this.handleFetchError(error);
    }
  }

  async updateDownload(id: string, download: {
    strDisplayName: string;
    bolisActive: boolean;
  }): Promise<FileDownload> {
    try {
      const response = await fetch(`${API_BASE_URL}/Download/documents/${id}`, {
        method: 'PUT',
        headers: this.getAuthHeaders(),
        body: JSON.stringify(download),
      });
      return this.handleResponse<FileDownload>(response);
    } catch (error) {
      return this.handleFetchError(error);
    }
  }

  async deleteDownload(id: string): Promise<void> {
    try {
      const response = await fetch(`${API_BASE_URL}/Download/documents/${id}`, {
        method: 'DELETE',
        headers: this.getAuthHeaders(),
      });
      if (!response.ok) {
        // Try to get the error message from the response
        const errorData = await response.json().catch(() => ({}));
        const errorMessage = errorData.message || `Failed to delete download with ID ${id}`;
        throw new Error(errorMessage);
      }
    } catch (error) {
      return this.handleFetchError(error);
    }
  }

  async downloadFile(id: string): Promise<void> {
    try {
      const response = await fetch(`${API_BASE_URL}/Download/documents/${id}/download`, {
        method: 'GET',
        headers: this.getAuthHeaders(),
      });
      
      if (!response.ok) {
        throw new Error(`Failed to download file with ID ${id}`);
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `download-${id}.pdf`); // You might want to get the actual filename
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      return this.handleFetchError(error);
    }
  }

  // Add downloadDocument method for backward compatibility
  async downloadDocument(id: string): Promise<void> {
    return this.downloadFile(id);
  }

  // Circular Module Methods
  async getAllCirculars(): Promise<Circular[]> {
    try {
      const response = await fetch(`${API_BASE_URL}/Circular`, {
        method: 'GET',
        headers: this.getAuthHeaders(),
      });
      return this.handleResponse<Circular[]>(response);
    } catch (error) {
      return this.handleFetchError(error);
    }
  }

  async getCircularById(id: string): Promise<Circular> {
    try {
      const response = await fetch(`${API_BASE_URL}/Circular/${id}`, {
        method: 'GET',
        headers: this.getAuthHeaders(),
      });
      return this.handleResponse<Circular>(response);
    } catch (error) {
      return this.handleFetchError(error);
    }
  }

  async uploadCircular(formData: FormData): Promise<Circular> {
    const token = localStorage.getItem('token');
    const headers: HeadersInit = {
      ...(token && { Authorization: `Bearer ${token}` }),
    };

    const response = await fetch(`${API_BASE_URL}/Circular/upload`, {
      method: 'POST',
      headers,
      body: formData,
    });

    return this.handleResponse<Circular>(response);
  }

  async updateCircular(id: string, circularDate: string, issuedBy: string, refNo: string, subject: string, file?: File): Promise<Circular> {
    try {
      const formData = new FormData();
      formData.append('circularDate', circularDate);
      formData.append('issuedBy', issuedBy);
      formData.append('refNo', refNo);
      formData.append('subject', subject);
      if (file) {
        formData.append('file', file);
      }

      const token = localStorage.getItem('token');
      const headers: HeadersInit = {
        ...(token && { Authorization: `Bearer ${token}` }),
      };

      const response = await fetch(`${API_BASE_URL}/Circular/${id}`, {
        method: 'PUT',
        headers,
        body: formData,
      });
      return this.handleResponse<Circular>(response);
    } catch (error) {
      return this.handleFetchError(error);
    }
  }

  async deleteCircular(id: string): Promise<void> {
    try {
      const response = await fetch(`${API_BASE_URL}/Circular/${id}`, {
        method: 'DELETE',
        headers: this.getAuthHeaders(),
      });
      if (!response.ok) {
        // Try to get the error message from the response
        const errorData = await response.json().catch(() => ({}));
        const errorMessage = errorData.message || `Failed to delete circular with ID ${id}`;
        throw new Error(errorMessage);
      }
    } catch (error) {
      return this.handleFetchError(error);
    }
  }

  async updateCircularStatus(id: string, bolisActive: boolean): Promise<Circular> {
    try {
      const response = await fetch(`${API_BASE_URL}/Circular/${id}/status`, {
        method: 'PUT',
        headers: {
          ...this.getAuthHeaders(),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ bolisActive }),
      });
      return this.handleResponse<Circular>(response);
    } catch (error) {
      return this.handleFetchError(error);
    }
  }

  async downloadCircular(id: string): Promise<void> {
    try {
      const response = await fetch(`${API_BASE_URL}/Circular/${id}/download`, {
        method: 'GET',
        headers: this.getAuthHeaders(),
      });
      
      if (!response.ok) {
        throw new Error(`Failed to download circular with ID ${id}`);
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `circular-${id}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      return this.handleFetchError(error);
    }
  }

  // Contact Module Methods
  async submitContact(contactData: {
    strName: string;
    strEmail: string;
    strSubject: string;
    strMessage: string;
    strPhone: string;
    strSource?: string;
  }): Promise<{ message: string; contact: Contact }> {
    try {
      const response = await fetch(`${API_BASE_URL}/Contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(contactData),
      });
      return this.handleResponse<{ message: string; contact: Contact }>(response);
    } catch (error) {
      return this.handleFetchError(error);
    }
  }

  async getAllContacts(): Promise<Contact[]> {
    try {
      const response = await fetch(`${API_BASE_URL}/Contact`, {
        method: 'GET',
        headers: this.getAuthHeaders(),
      });
      const result = await this.handleResponse<{ contacts: Contact[]; unreadCount: number; totalCount: number }>(response);
      return result.contacts;
    } catch (error) {
      return this.handleFetchError(error);
    }
  }

  async getContactById(id: string): Promise<Contact> {
    try {
      const response = await fetch(`${API_BASE_URL}/Contact/${id}`, {
        method: 'GET',
        headers: this.getAuthHeaders(),
      });
      return this.handleResponse<Contact>(response);
    } catch (error) {
      return this.handleFetchError(error);
    }
  }

  async markContactAsRead(id: string, readBy: string): Promise<{ message: string }> {
    try {
      const response = await fetch(`${API_BASE_URL}/Contact/${id}/read`, {
        method: 'POST',
        headers: this.getAuthHeaders(),
        body: JSON.stringify({ strReadBy: readBy }),
      });
      return this.handleResponse<{ message: string }>(response);
    } catch (error) {
      return this.handleFetchError(error);
    }
  }

  async replyToContact(id: string, replyMessage: string, repliedBy: string): Promise<{ message: string }> {
    try {
      const response = await fetch(`${API_BASE_URL}/Contact/${id}/reply`, {
        method: 'POST',
        headers: this.getAuthHeaders(),
        body: JSON.stringify({ 
          strReplyMessage: replyMessage, 
          strRepliedBy: repliedBy 
        }),
      });
      return this.handleResponse<{ message: string }>(response);
    } catch (error) {
      return this.handleFetchError(error);
    }
  }

  async deleteContact(id: string): Promise<{ message: string }> {
    try {
      const response = await fetch(`${API_BASE_URL}/Contact/${id}`, {
        method: 'DELETE',
        headers: this.getAuthHeaders(),
      });
      return this.handleResponse<{ message: string }>(response);
    } catch (error) {
      return this.handleFetchError(error);
    }
  }

  async getUnreadContactCount(): Promise<number> {
    try {
      const response = await fetch(`${API_BASE_URL}/Contact/unread-count`, {
        method: 'GET',
        headers: this.getAuthHeaders(),
      });
      const result = await this.handleResponse<{ unreadCount: number }>(response);
      return result.unreadCount;
    } catch (error) {
      return this.handleFetchError(error);
    }
  }

  // Other Files Module Methods
  async getAllOtherFiles(): Promise<OtherFile[]> {
    try {
      const response = await fetch(`${API_BASE_URL}/OtherFile`, {
        method: 'GET',
        headers: this.getAuthHeaders(),
      });
      return this.handleResponse<OtherFile[]>(response);
    } catch (error) {
      return this.handleFetchError(error);
    }
  }

  async getOtherFilesByModuleType(moduleType: string): Promise<OtherFile[]> {
    try {
      const response = await fetch(`${API_BASE_URL}/OtherFile/module/${encodeURIComponent(moduleType)}`, {
        method: 'GET',
        headers: this.getAuthHeaders(),
      });
      return this.handleResponse<OtherFile[]>(response);
    } catch (error) {
      return this.handleFetchError(error);
    }
  }

  async getOtherFileById(id: string): Promise<OtherFile> {
    try {
      const response = await fetch(`${API_BASE_URL}/OtherFile/${id}`, {
        method: 'GET',
        headers: this.getAuthHeaders(),
      });
      return this.handleResponse<OtherFile>(response);
    } catch (error) {
      return this.handleFetchError(error);
    }
  }

  async uploadOtherFile(formData: FormData): Promise<OtherFile> {
    const token = localStorage.getItem('token');
    const headers: HeadersInit = {
      ...(token && { Authorization: `Bearer ${token}` }),
    };

    const response = await fetch(`${API_BASE_URL}/OtherFile/upload`, {
      method: 'POST',
      headers,
      body: formData,
    });

    return this.handleResponse<OtherFile>(response);
  }

  async updateOtherFile(id: string, title: string, file?: File): Promise<OtherFile> {
    try {
      const formData = new FormData();
      formData.append('title', title);
      if (file) {
        formData.append('file', file);
      }

      const token = localStorage.getItem('token');
      const headers: HeadersInit = {
        ...(token && { Authorization: `Bearer ${token}` }),
      };

      const response = await fetch(`${API_BASE_URL}/OtherFile/${id}`, {
        method: 'PUT',
        headers,
        body: formData,
      });
      return this.handleResponse<OtherFile>(response);
    } catch (error) {
      return this.handleFetchError(error);
    }
  }

  async deleteOtherFile(id: string): Promise<void> {
    try {
      const response = await fetch(`${API_BASE_URL}/OtherFile/${id}`, {
        method: 'DELETE',
        headers: this.getAuthHeaders(),
      });
      if (!response.ok) {
        // Try to get the error message from the response
        const errorData = await response.json().catch(() => ({}));
        const errorMessage = errorData.message || `Failed to delete other file with ID ${id}`;
        throw new Error(errorMessage);
      }
    } catch (error) {
      return this.handleFetchError(error);
    }
  }

  async downloadOtherFile(id: string): Promise<void> {
    try {
      const response = await fetch(`${API_BASE_URL}/OtherFile/${id}/download`, {
        method: 'GET',
        headers: this.getAuthHeaders(),
      });
      
      if (!response.ok) {
        throw new Error(`Failed to download other file with ID ${id}`);
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `otherfile-${id}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      return this.handleFetchError(error);
    }
  }

  // Statistics Methods
  async getDownloadHistory(categoryId?: string, documentId?: string): Promise<FileDownloadHistory[]> {
    try {
      let url = `${API_BASE_URL}/FileManagement/download-history`;
      const params = new URLSearchParams();
      if (categoryId) params.append('categoryId', categoryId);
      if (documentId) params.append('documentId', documentId);
      if (params.toString()) url += `?${params.toString()}`;

      const response = await fetch(url, {
        method: 'GET',
        headers: this.getAuthHeaders(),
      });
      return this.handleResponse<FileDownloadHistory[]>(response);
    } catch (error) {
      return this.handleFetchError(error);
    }
  }

  async getUserDownloads(): Promise<FileDownloadHistory[]> {
    try {
      const response = await fetch(`${API_BASE_URL}/FileManagement/user-downloads`, {
        method: 'GET',
        headers: this.getAuthHeaders(),
      });
      return this.handleResponse<FileDownloadHistory[]>(response);
    } catch (error) {
      return this.handleFetchError(error);
    }
  }

  async getStatistics(): Promise<any> {
    try {
      const [downloadStats, storageStats] = await Promise.all([
        fetch(`${API_BASE_URL}/FileManagement/statistics`, {
          method: 'GET',
          headers: this.getAuthHeaders(),
        }).then(res => this.handleResponse<any>(res)),
        fetch(`${API_BASE_URL}/FileManagement/storage/statistics`, {
          method: 'GET',
          headers: this.getAuthHeaders(),
        }).then(res => this.handleResponse<any>(res))
      ]);

      // Get contact statistics
      const contactResponse = await fetch(`${API_BASE_URL}/Contact`, {
        method: 'GET',
        headers: this.getAuthHeaders(),
      });
      const contactStats = await this.handleResponse<{ contacts: Contact[]; unreadCount: number; totalCount: number }>(contactResponse);

      interface StorageModule {
        Module: string;
        Count: number;
      }

      return {
        totalDownloads: downloadStats.TotalDownloads || 0,
        totalCategories: downloadStats.TotalCategories || 0,
        totalCirculars: storageStats.StorageByModule?.find((m: StorageModule) => m.Module === 'Circulars')?.Count || 0,
        totalContacts: contactStats.totalCount || 0,
        unreadContacts: contactStats.unreadCount || 0,
        documentsThisMonth: downloadStats.ThisMonthDownloads || 0,
        categoriesThisWeek: 0,
        circularsToday: 0,
        downloadsToday: downloadStats.TodayDownloads || 0,
        downloadsThisWeek: downloadStats.ThisWeekDownloads || 0,
        recentDownloads: downloadStats.TopDownloadedItems || [],
        fileTypeDistribution: downloadStats.DownloadsByType || []
      };
    } catch (error) {
      return this.handleFetchError(error);
    }
  }
}

export const apiService = new ApiService(); 